import React, { useEffect } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { t } from "i18next";

const SupplierLineChart = ({ chartData, chartHeight }) => {
  useEffect(() => {
    // Create root element
    const root = am5.Root.new("supplierLineChartDiv");

    const myTheme = am5.Theme.new(root);

    // Move minor label a bit down
    myTheme.rule("AxisLabel", ["minor"]).setAll({
      dy: 1,
    });

    // Tweak minor grid opacity
    myTheme.rule("Grid", ["minor"]).setAll({
      strokeOpacity: 0.08,
    });

    // Set themes
    root.setThemes([am5themes_Animated.new(root), myTheme]);

    // Create chart
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: "panX",
        wheelY: "zoomX",
        paddingLeft: 0,
      })
    );

    // Add cursor
    const cursor = chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        behavior: "zoomX",
      })
    );
    cursor.lineY.set("visible", false);

    // Format the data for the chart (convert date to timestamp)
    const formattedData = chartData?.map((item) => ({
      date: new Date(item.date).getTime(), // Assuming date is in milliseconds
      orders: item.orders, // Keeping your orders structure
      earnings: item.earnings, // Keeping your earnings structure
    }));

    // Create axes for Orders
    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        maxDeviation: 0,
        baseInterval: {
          timeUnit: "day",
          count: 1,
        },
        renderer: am5xy.AxisRendererX.new(root, {
          minorGridEnabled: true,
          minGridDistance: 200,
          minorLabelsEnabled: true,
        }),
        tooltip: am5.Tooltip.new(root, {}),
      })
    );

    const yAxisOrders = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
      })
    );

    // Create secondary Y-axis for Earnings
    const yAxisEarnings = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, { opposite: true }),
        extraMin: 0.1, // Adjust scale if necessary
      })
    );

    // Orders Series
    const ordersSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: t("totalOrders"),
        xAxis: xAxis,
        yAxis: yAxisOrders,
        valueYField: "orders",
        valueXField: "date",
        stroke: am5.color("#FF5733"),
        tooltip: am5.Tooltip.new(root, {
          labelText: "{valueY} Orders",
        }),
      })
    );

    ordersSeries.bullets.push(() => {
      const bulletCircle = am5.Circle.new(root, {
        radius: 5,
        fill: ordersSeries.get("fill"),
      });
      return am5.Bullet.new(root, {
        sprite: bulletCircle,
      });
    });

    // Earnings Series
    const earningsSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: t("totalEarnings"),
        xAxis: xAxis,
        yAxis: yAxisEarnings,
        valueYField: "earnings",
        valueXField: "date",
        stroke: am5.color("#7922D8"),
        tooltip: am5.Tooltip.new(root, {
          labelText: "{valueY} Earnings",
        }),
      })
    );

    earningsSeries.bullets.push(() => {
      const bulletCircle = am5.Circle.new(root, {
        radius: 5,
        fill: earningsSeries.get("fill"),
      });
      return am5.Bullet.new(root, {
        sprite: bulletCircle,
      });
    });

    // Add scrollbar
    chart.set(
      "scrollbarX",
      am5.Scrollbar.new(root, {
        orientation: "horizontal",
      })
    );

    // Set data to both series
    ordersSeries.data.setAll(formattedData);
    earningsSeries.data.setAll(formattedData);

    // Make stuff animate on load
    ordersSeries.appear(1000);
    earningsSeries.appear(1000);
    chart.appear(1000, 100);

    // Cleanup function
    return () => {
      root.dispose();
    };
  }, [chartData]);

  return (
    <div
      id="supplierLineChartDiv"
      style={{ width: "100%", height: chartHeight || "400px" }}
    />
  );
};

export default SupplierLineChart;
